import React, {useEffect, useRef, useState} from 'react';
import {Template} from "../../../components/template/template.jsx";
import {useQuery} from "@tanstack/react-query";
import myUserService from "../../../services/web/my-user-service.js";
import MyUserName from "./components/my-user-name.jsx";
import MyUserInfo from "./components/my-user-info.jsx";
import projectUtils from "../../../utils/project-utils.js";
import MyUserNameWar from "./components/my-user-namewar.jsx";
import MyUserEmail from "./components/my-user-email.jsx";
import MyUserDevices from "./components/my-user-devices.jsx";
import MyUserShared from "./components/my-user-shared.jsx";
import MyUserSignature from "./components/my-user-signature.jsx";
import MyUserPassword from "./components/my-user-password.jsx";
import MyUserTrustedDevices from "./components/my-user-trusted-devices.jsx";
import MyUserTwoFactor from "./components/my-user-two-factor.jsx";
import MyUserTotpConfig from "./components/my-user-totp-config.jsx";
import {useSearchParams} from "react-router";
import {FaTriangleExclamation} from "react-icons/fa6";
import {Card} from "../../../components/card/card.jsx";
import Button from "../../../components/button/button.jsx";
import {toast} from "react-toastify";
import AlertMessages from "../../../components/messages/alert-message.js";
import MyUserPin from "./components/my-user-pin.jsx";
import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";
import {navigateHook} from "../../../hooks/use-navigate.js";
import {queryClient} from "../../../services/query-client.js";
import {SESSION_QUERY_KEY} from "../../../services/session.js";

const MySwal = withReactContent(Swal);

const MyUser = () => {
    const navigate = navigateHook();

    const [target, setTarget] = useState("info");
    const [searchParams, setSearchParams] = useSearchParams();
    const [isLoading, setIsLoading] = useState(false);

    const showAlert = useRef(true);

    const {data, refetch} = useQuery({
        queryFn: () =>  myUserService.get().then((data) => {
            data.devices = data.devices.map(d =>
                ({
                    ...d,
                    value: d.id,
                    label: projectUtils.formatPhone(d.phone)
                })
            );

            data.mainDevice = data.devices.find(d => d.main);

            return data;
        }),
        queryKey: ['my-user']
    });

    const clearSessionCache = () => {
        queryClient.removeQueries({queryKey: SESSION_QUERY_KEY});
    };

    const handleTarget = (nextTarget) => {
        setTarget(nextTarget);

        if (nextTarget !== "info" || !searchParams.has("target")) return;

        const nextParams = new URLSearchParams(searchParams);
        nextParams.delete("target");
        setSearchParams(nextParams, {replace: true});
    };

    useEffect(() => {
        if (!data) return;

        if (data?.dataIsStale && showAlert.current) {
            MySwal.fire({
                type: 'warning',
                title: 'Atenção',
                text: 'Verifique seu cadastro.',
                showCancelButton: true,
                confirmButtonColor: '#063A6C',
                confirmButtonText: 'Verificar',
                cancelButtonText: 'Agora não',
                closeOnConfirm: true,
                allowOutsideClick: false,
                reverseButtons: true
            }).then(({isConfirmed}) => {
                if (!isConfirmed) {
                    setIsLoading(true);

                    myUserService.ignoreStaleData()
                        .then(() => {
                            clearSessionCache();
                            navigate("/home");
                        })
                        .catch(error => {
                            console.error(error);
                            projectUtils.swalErrorMessage("", "Não foi possível prosseguir.", "Ok");
                        })
                        .finally(() => setIsLoading(false));
                } else {
                    showAlert.current = false;
                }
            });

            return;
        }

        const target = searchParams.get("target");
        if (target) setTarget(target);
    }, [data, searchParams]);

    const renderTarget = () => {
        const components = {
            "info": <MyUserInfo data={data} refetch={refetch} setTarget={handleTarget} />,
            "name": <MyUserName name={data?.name} refetch={refetch} setTarget={handleTarget} setIsLoading={setIsLoading}/>,
            "nameWar": <MyUserNameWar nameWar={data?.nameWar} refetch={refetch} setTarget={handleTarget} setIsLoading={setIsLoading}/>,
            "email": <MyUserEmail email={data?.email} refetch={refetch} setTarget={handleTarget} setIsLoading={setIsLoading}/>,
            "devices": <MyUserDevices devicesFetch={data?.devices} refetch={refetch} setTarget={handleTarget} user={data}/>,
            "shared": <MyUserShared user={data} refetch={refetch} setTarget={handleTarget} setIsLoading={setIsLoading}/>,
            "signature": <MyUserSignature signature={data?.signatureDefault} refetch={refetch} setTarget={handleTarget} setIsLoading={setIsLoading}/>,
            "password": <MyUserPassword refetch={refetch} setTarget={handleTarget} setIsLoading={setIsLoading}/>,
            "twoFactor": <MyUserTwoFactor refetch={refetch} setTarget={handleTarget} user={data}/>,
            "totpConfig": <MyUserTotpConfig refetch={refetch} setTarget={handleTarget} setIsLoading={setIsLoading}/>,
            "trustedDevices": <MyUserTrustedDevices devices={data?.trustedDevices} refetch={refetch} setTarget={handleTarget} setIsLoading={setIsLoading}/>,
            "pin": <MyUserPin devices={data?.pin} refetch={refetch} setTarget={handleTarget} setIsLoading={setIsLoading}/>,
        }

        return components[target] || null;
    }

    const update = () => {
        setIsLoading(true);

        myUserService.declareUpToDate()
            .then(() => {
                showAlert.current = false;
                projectUtils.success(false, "Atualizado com sucesso");
                clearSessionCache();
                return refetch();
            })
            .catch(error => {
                let swalMessage = 'Não foi possível prosseguir.';
                console.error(error);
                const {response} = error;
                if (response) {
                    const {message} = response.data;
                    if (message && typeof message == 'string') swalMessage = AlertMessages[message];
                }
                toast.error(swalMessage);
            })
            .finally(() => setIsLoading(false));
    }

    return (
        <Template isLoading={isLoading}>
            <Template.Header>
                <Template.Title>Meu cadastro</Template.Title>
            </Template.Header>

            <Template.Body>
                <div className={"max-w-[56rem] ml-auto mr-auto space-y-2"}>
                    {
                        data?.dataIsStale &&
                        <Card>
                            <Card.Body>
                                <div className="flex justify-between">
                                    <div className="flex items-center gap-2">
                                        <FaTriangleExclamation size={"20px"} color={"#FF9900"}/>
                                        <span className="text-[14px]">
                                        Verifique e atualize seu cadastro para garantir que suas informações estejam corretas.
                                    </span>
                                    </div>
                                    <div className="flex justify-end">
                                        <Button target="primary" onClick={update} actionFeedbackType="save" loadingText="Atualizando..." successText="Atualizado com sucesso">
                                            Confirmar
                                        </Button>
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                    }

                    {renderTarget()}
                </div>
            </Template.Body>
        </Template>
    );
};

export default MyUser;